/* eslint-disable no-barrel-files/no-barrel-files -- Compatibility layer that re-exports UIKit Typography sub-components */

import { Typography as CompassTypography } from "@circleco/compass/components/Typography";
import { allowedClassesFor } from "@circleco/compass/helpers/allowedClassesFor";

import { LabelLg as UIKitLabelLg } from "@/react/components/shared/uikit/Typography/components/Label/LabelLg";
import type { LabelLgProps } from "@/react/components/shared/uikit/Typography/components/Label/LabelLg";
import { LabelMd as UIKitLabelMd } from "@/react/components/shared/uikit/Typography/components/Label/LabelMd";
import type { LabelMdProps } from "@/react/components/shared/uikit/Typography/components/Label/LabelMd";
import { LabelSm as UIKitLabelSm } from "@/react/components/shared/uikit/Typography/components/Label/LabelSm";
import type { LabelSmProps } from "@/react/components/shared/uikit/Typography/components/Label/LabelSm";
import { LabelXl as UIKitLabelXl } from "@/react/components/shared/uikit/Typography/components/Label/LabelXl";
import type { LabelXlProps } from "@/react/components/shared/uikit/Typography/components/Label/LabelXl";
import { LabelXs as UIKitLabelXs } from "@/react/components/shared/uikit/Typography/components/Label/LabelXs";
import type { LabelXsProps } from "@/react/components/shared/uikit/Typography/components/Label/LabelXs";
import { LabelXsPlus as UIKitLabelXsPlus } from "@/react/components/shared/uikit/Typography/components/Label/LabelXsPlus";
import type { LabelXsPlusProps } from "@/react/components/shared/uikit/Typography/components/Label/LabelXsPlus";
import { LabelXxs as UIKitLabelXxs } from "@/react/components/shared/uikit/Typography/components/Label/LabelXxs";
import type { LabelXxsProps } from "@/react/components/shared/uikit/Typography/components/Label/LabelXxs";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { mapColor, extractExtraClasses } from "./colorMap";
import { useTypographyFlag, filterClassName, stripUIKitProps } from "./shared";
import type { TypographyElement } from "./shared";

export type {
  LabelXlProps,
  LabelLgProps,
  LabelMdProps,
  LabelSmProps,
  LabelXsProps,
  LabelXsPlusProps,
  LabelXxsProps,
};

export const LabelXl = ({
  children,
  color,
  truncate,
  leading,
  textAlign,
  as = "span",
  "data-testid": dataTestId,
  ...rest
}: LabelXlProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitLabelXl
        color={color}
        truncate={truncate}
        leading={leading}
        textAlign={textAlign}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitLabelXl>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="label-xl"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "span"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        classNames(filterClassName(textAlign), extractExtraClasses(color)),
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const LabelLg = ({
  children,
  color,
  truncate,
  leading,
  textAlign,
  as = "span",
  "data-testid": dataTestId,
  ...rest
}: LabelLgProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitLabelLg
        color={color}
        truncate={truncate}
        leading={leading}
        textAlign={textAlign}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitLabelLg>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="label-lg"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "span"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        classNames(filterClassName(textAlign), extractExtraClasses(color)),
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const LabelMd = ({
  children,
  color,
  truncate,
  leading,
  textTransform,
  as = "span",
  "data-testid": dataTestId,
  ...rest
}: LabelMdProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitLabelMd
        color={color}
        truncate={truncate}
        leading={leading}
        textTransform={textTransform}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitLabelMd>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="label-md"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "span"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        classNames(filterClassName(textTransform), extractExtraClasses(color)),
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const LabelSm = ({
  children,
  color,
  truncate,
  leading,
  textTransform,
  as = "span",
  "data-testid": dataTestId,
  ...rest
}: LabelSmProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitLabelSm
        color={color}
        truncate={truncate}
        leading={leading}
        textTransform={textTransform}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitLabelSm>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="label-sm"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "span"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        classNames(filterClassName(textTransform), extractExtraClasses(color)),
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const LabelXs = ({
  children,
  color,
  truncate,
  leading,
  textTransform,
  as = "span",
  "data-testid": dataTestId,
  ...rest
}: LabelXsProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitLabelXs
        color={color}
        truncate={truncate}
        leading={leading}
        textTransform={textTransform}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitLabelXs>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="label-xs"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "span"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        classNames(filterClassName(textTransform), extractExtraClasses(color)),
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const LabelXsPlus = ({
  children,
  color,
  truncate,
  leading,
  textTransform,
  as = "span",
  "data-testid": dataTestId,
  ...rest
}: LabelXsPlusProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitLabelXsPlus
        color={color}
        truncate={truncate}
        leading={leading}
        textTransform={textTransform}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitLabelXsPlus>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="label-sm"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "span"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        classNames(filterClassName(textTransform), extractExtraClasses(color)),
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};

export const LabelXxs = ({
  children,
  color,
  truncate,
  leading,
  textTransform = "uppercase",
  as = "span",
  "data-testid": dataTestId,
  ...rest
}: LabelXxsProps) => {
  if (!useTypographyFlag()) {
    return (
      <UIKitLabelXxs
        color={color}
        truncate={truncate}
        leading={leading}
        textTransform={textTransform}
        as={as as string}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </UIKitLabelXxs>
    );
  }

  return (
    <CompassTypography
      {...stripUIKitProps(rest)}
      variant="label-xs-uppercase"
      color={mapColor(color)}
      component={(as as TypographyElement) ?? "span"}
      truncate={truncate}
      className={allowedClassesFor(
        "Typography",
        classNames(
          filterClassName(
            textTransform !== "uppercase" ? textTransform : undefined,
          ),
          extractExtraClasses(color),
        ),
      )}
      data-testid={dataTestId}
    >
      {children}
    </CompassTypography>
  );
};
